import type { FC } from 'react'

import {
  intentDesciptionText,
  queuePositionNumber,
  queuePositionText,
} from '@liveHelper/components/Dialing/Dialing.twind'
import { PrivacyAlert } from '@liveHelper/components/PrivacyAlert'
import { ChatButton } from '@liveHelper/components/shared/ChatButton'
import { TripleDotLoader } from '@liveHelper/components/shared/TripleDotLoader'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  hasSentMessageSelector,
  isTransferringSelector,
  queuePositionSelector,
  uiStateSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { ChatThemeContext } from '@oneToOne/shared/components/chat/theme/ChatThemeContext'
import { ChatInput } from '@oneToOne/shared/components/twind/ChatInput'

import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

/**
 *  Dialing Footer when expanded
 */
export const DialingFooter: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const uiState = useStore(uiStateSelector)
  const queuePosition = useStore(queuePositionSelector)
  const { sendTextMessage } = useStore(visitorPrivateActionsSelector)
  const hasSentMessage = useStore(hasSentMessageSelector)
  const isTransferring = useStore(isTransferringSelector)

  const chatChannelConfig = useChatChannelConfig()

  if (uiState === 'dialing_expanded') {
    if (isTransferring) {
      return (
        <div
          className={tw?.(
            intentDesciptionText,
            'text-sm',
            'mt-3',
            'mx-auto',
            'text-center',
            'w-72',
          )}
        >
          {t(
            'oneToOne:Keep this window open to avoid losing your place in the queue.',
          )}
        </div>
      )
    }

    return (
      <>
        {queuePosition && queuePosition > 1 ? (
          <div
            className={tw?.('flex', 'justify-center', 'items-center', 'gap-3')}
          >
            <div
              className={tw?.(queuePositionText, 'text-base', 'font-medium')}
            >
              {t('oneToOne:Queue position')}:
            </div>
            <div
              className={tw?.(
                queuePositionNumber,
                'rounded-md',
                'flex',
                'items-center',
                'justify-center',
                'font-semibold',
              )}
            >
              {queuePosition}
            </div>
          </div>
        ) : queuePosition ? (
          <TripleDotLoader />
        ) : null}
        {!hasSentMessage ? (
          <>
            <div
              className={tw?.(
                intentDesciptionText,
                'text-sm',
                'mt-3',
                'mx-auto',
                'text-center',
                'w-72',
              )}
            >
              {t('oneToOne:To be assisted sooner,')}
            </div>
            <div
              className={tw?.(
                intentDesciptionText,
                'text-sm',
                'mx-auto',
                'text-center',
                'w-72',
                'mb-2',
              )}
            >
              {t('oneToOne:share the reason for your call')}
            </div>
            <ChatThemeContext.Provider value={{ theme: 'transparent' }}>
              <ChatInput
                placeholder={t(
                  'oneToOne:I am looking for a present for my dad',
                )}
                onEnter={(message) => {
                  sendTextMessage?.(message)
                }}
                onChange={() => null}
              />
            </ChatThemeContext.Provider>
          </>
        ) : (
          <div className={tw?.('flex', 'justify-center', 'gap-12', 'mt-3')}>
            <ChatButton
              isMinimized={false}
              chatEnabled={false}
              showUnreadMessageNumber={true}
            />
          </div>
        )}
        {!chatChannelConfig?.config?.capture_lead_information ? (
          <PrivacyAlert hasContainer={false} />
        ) : null}
      </>
    )
  }

  return null
}
